/**
 * StagesConfig — manage pipeline stages.
 * Admin can: create / edit / archive / reorder.
 * Reordering uses up/down arrows; backend rebalances positions on save.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Edit3, Plus, RefreshCw, X } from 'lucide-react';
import { createStage, getStages, updateStage } from '../api';
import type { Stage } from '../types';

export default function StagesConfig() {
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Stage | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await getStages(includeArchived);
    if (!res.success || !res.data) {
      setError(res.error || 'Failed to load stages.');
      setStages([]);
    } else {
      setStages(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [includeArchived]);

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 3000);
  };

  const handleArchiveToggle = async (stage: Stage) => {
    const r = await updateStage(stage.id, { archived: !stage.archived_at });
    if (!r.success) {
      showToast(r.error || 'Failed to update');
      return;
    }
    await load();
    showToast(stage.archived_at ? 'Stage unarchived.' : 'Stage archived.');
  };

  const handleReorder = async (stage: Stage, direction: 'up' | 'down') => {
    const active = stages.filter((s) => !s.archived_at);
    const idx = active.findIndex((s) => s.id === stage.id);
    if (idx < 0) return;
    const swapWith = direction === 'up' ? active[idx - 1] : active[idx + 1];
    if (!swapWith) return;
    // Swap positions
    await updateStage(stage.id, { position: swapWith.position });
    await updateStage(swapWith.id, { position: stage.position });
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Pipeline stages</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            The customer-facing journey from new signup through activation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show archived
          </label>
          <button
            onClick={load}
            className="p-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add stage
          </button>
        </div>
      </div>

      {toast && (
        <div className="mb-3 px-3 py-1.5 bg-green-50 border border-green-200 text-green-800 text-sm rounded">
          {toast}
        </div>
      )}

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
          {error}
        </div>
      ) : loading ? (
        <div className="text-center py-8 text-gray-500">
          <RefreshCw className="w-4 h-4 inline-block animate-spin mr-2" />
          Loading…
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Pos</th>
                <th className="px-3 py-2 text-left font-medium">Slug</th>
                <th className="px-3 py-2 text-left font-medium">Display name</th>
                <th className="px-3 py-2 text-left font-medium">Duration</th>
                <th className="px-3 py-2 text-left font-medium">Terminal</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {stages.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500 italic">
                    No stages yet.
                  </td>
                </tr>
              ) : (
                stages.map((s) => {
                  const isActive = !s.archived_at;
                  const activeStages = stages.filter((x) => !x.archived_at);
                  const activeIdx = activeStages.findIndex((x) => x.id === s.id);
                  return (
                    <tr
                      key={s.id}
                      className={s.archived_at ? 'bg-gray-50' : ''}
                    >
                      <td className="px-3 py-2 text-gray-700">{s.position}</td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-700">
                        {s.slug}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {s.display_name}
                        {s.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {s.description}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {s.expected_duration_hours
                          ? `${s.expected_duration_hours}h`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {s.is_terminal_good && (
                          <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 rounded">
                            Good
                          </span>
                        )}
                        {s.is_terminal_bad && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 rounded">
                            Bad
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">
                        {s.archived_at ? (
                          <span className="text-xs text-gray-500 italic">Archived</span>
                        ) : (
                          <span className="text-xs text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          {isActive && (
                            <>
                              <button
                                onClick={() => handleReorder(s, 'up')}
                                disabled={activeIdx === 0}
                                className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                                title="Move up"
                              >
                                <ChevronUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReorder(s, 'down')}
                                disabled={activeIdx === activeStages.length - 1}
                                className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                                title="Move down"
                              >
                                <ChevronDown className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setEditing(s)}
                            className="p-1 text-gray-500 hover:text-gray-900"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleArchiveToggle(s)}
                            className="p-1 text-gray-500 hover:text-gray-900"
                            title={s.archived_at ? 'Unarchive' : 'Archive'}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <StageEditor
          mode="create"
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
            showToast('Stage created.');
          }}
        />
      )}

      {editing && (
        <StageEditor
          mode="edit"
          stage={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            showToast('Stage updated.');
          }}
        />
      )}
    </div>
  );
}

function StageEditor({
  mode,
  stage,
  onCancel,
  onSaved,
}: {
  mode: 'create' | 'edit';
  stage?: Stage;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Partial<Stage>>({
    slug: stage?.slug || '',
    display_name: stage?.display_name || '',
    description: stage?.description || '',
    position: stage?.position ?? 100,
    expected_duration_hours: stage?.expected_duration_hours ?? null,
    is_terminal_good: stage?.is_terminal_good ?? false,
    is_terminal_bad: stage?.is_terminal_bad ?? false,
    color_hex: stage?.color_hex || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    const payload: any = { ...form };
    if (payload.description === '') payload.description = null;
    if (payload.color_hex === '') payload.color_hex = null;

    const r =
      mode === 'create' ? await createStage(payload) : await updateStage(stage!.id, payload);
    setSaving(false);
    if (!r.success) {
      setErr(r.error || 'Save failed');
      return;
    }
    onSaved();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          {mode === 'create' ? 'New stage' : `Edit ${stage?.display_name}`}
        </h3>
        {err && (
          <div className="mb-3 px-3 py-1.5 bg-red-50 border border-red-200 text-red-800 text-sm rounded">
            {err}
          </div>
        )}
        <div className="space-y-3">
          <Field label="Slug (machine name, cannot collide)">
            <input
              type="text"
              value={form.slug || ''}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              disabled={mode === 'edit'}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono disabled:bg-gray-50 disabled:text-gray-500"
            />
          </Field>
          <Field label="Display name (customer-facing in Phase 2)">
            <input
              type="text"
              value={form.display_name || ''}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </Field>
          <Field label="Description (admin-facing)">
            <textarea
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Position">
              <input
                type="number"
                value={form.position ?? ''}
                onChange={(e) =>
                  setForm({ ...form, position: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
            </Field>
            <Field label="Expected duration (hours)">
              <input
                type="number"
                value={form.expected_duration_hours ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    expected_duration_hours: e.target.value === '' ? null : parseInt(e.target.value, 10),
                  })
                }
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
            </Field>
          </div>
          <Field label="Color (optional hex like #4f46e5)">
            <input
              type="text"
              value={form.color_hex || ''}
              onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono"
            />
          </Field>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={!!form.is_terminal_good}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_terminal_good: e.target.checked,
                    is_terminal_bad: e.target.checked ? false : form.is_terminal_bad,
                  })
                }
              />
              Terminal-good (e.g. Active Client)
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={!!form.is_terminal_bad}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_terminal_bad: e.target.checked,
                    is_terminal_good: e.target.checked ? false : form.is_terminal_good,
                  })
                }
              />
              Terminal-bad (e.g. Cancelled)
            </label>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || !form.slug || !form.display_name}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
